import { API_CONFIG, apiFetch, handleApiResponse, getPrismaticHeaders } from './api';
import { isCaptureEnabled, disableCapture, saveResponse, getResponse, hasResponse, isCachingEnabled } from './CacheService';

// API types
export interface MetricTimeseriesData {
  [key: string]: Array<[number, number]> | {
    [subKey: string]: Array<[number, number]>
  };
}

export interface MetricValue {
  timestamp: number;
  value: number;
}

export interface MetricValues {
  [key: string]: MetricValue[];
}

export type MetricsDataSource = 'api' | 'blob';

const METRICS_DATA_SOURCE_KEY = 'metricsDataSource';

export function initializeMetricsDataSource(): void {
  try {
    if (localStorage.getItem(METRICS_DATA_SOURCE_KEY) === null) {
      localStorage.setItem(METRICS_DATA_SOURCE_KEY, 'api');
    }
  } catch {
    // Silently fail if localStorage is not available
  }
}

export function getMetricsDataSource(): MetricsDataSource {
  try {
    const value = localStorage.getItem(METRICS_DATA_SOURCE_KEY);
    return value === 'blob' ? 'blob' : 'api';
  } catch {
    return 'api';
  }
}

export function setMetricsDataSource(source: MetricsDataSource): void {
  try {
    localStorage.setItem(METRICS_DATA_SOURCE_KEY, source);
  } catch {
    console.error('Failed to set metrics data source in localStorage');
  }
}

export class MetricsService {
  private prismaticApiKey: string;
  private prismaticApiUrl: string;

  constructor() {
    const savedSettings = sessionStorage.getItem('apiSettings');
    const apiSettings = savedSettings ? JSON.parse(savedSettings) : {};

    this.prismaticApiKey = localStorage.getItem('prismaticApiKey') || apiSettings.prismaticApiKey || '';
    this.prismaticApiUrl = localStorage.getItem('prismaticApiUrl') || apiSettings.prismaticApiUrl || '';
    
    console.debug('MetricsService initialized:', {
      prismaticApiKey: this.prismaticApiKey ? '***' : 'missing',
      prismaticApiUrl: this.prismaticApiUrl || 'missing'
    });
  }

  async getMetricsTimeseries(aggregation: string): Promise<MetricTimeseriesData> {
    const dataSource = getMetricsDataSource();

    if (dataSource === 'blob') {
      return this.getMetricsTimeseriesFromBlob(aggregation);
    }

    // Use Prismatic API
    if (!this.prismaticApiKey || !this.prismaticApiUrl) {
      console.error('Missing Prismatic API configuration');
      throw new Error('Missing Prismatic API credentials. Please configure them in settings.');
    }

    return this.getPrismaticTimeseries(aggregation);
  }

  private async getPrismaticTimeseries(aggregation: string): Promise<MetricTimeseriesData> {
    const params = new URLSearchParams({
      aggregation
    });

    const url = `${API_CONFIG.baseUrl}${API_CONFIG.endpoints.prismaticTimeseries}?${params.toString()}`;
    console.debug('Making Prismatic timeseries request to:', url);
    console.debug('Headers:', {
      'Content-Type': 'application/json',
      'API-Key': '***',
      'X-Prismatic-Api-Url': this.prismaticApiUrl
    });

    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: getPrismaticHeaders(this.prismaticApiKey, this.prismaticApiUrl)
      });

      const data = await handleApiResponse<MetricTimeseriesData>(response);
      console.debug('Prismatic API response data received:', Object.keys(data));
      return data;
    } catch (error) {
      console.error('Failed to fetch Prismatic timeseries:', error);
      throw new Error(error instanceof Error ? error.message : 'Failed to fetch Prismatic data');
    }
  }

  private async getMetricsTimeseriesFromBlob(aggregation: string): Promise<MetricTimeseriesData> {
    // Read instance (environment) and model (selectedModelLabel) from apiSettings
    let instance = '';
    let model = '';
    try {
      const savedSettings = sessionStorage.getItem('apiSettings');
      if (savedSettings) {
        const apiSettings = JSON.parse(savedSettings);
        instance = apiSettings.environment || '';
        model = apiSettings.selectedModelLabel || '';
      }
    } catch (e) {
      console.error('Error reading apiSettings from sessionStorage:', e);
    }

    if (!instance || !model) {
      throw new Error('Instance (environment) and model (selectedModelLabel) must be configured in API settings.');
    }

    const params = new URLSearchParams({ aggregation, instance, model });
    const url = `${API_CONFIG.baseUrl}${API_CONFIG.endpoints.metricsTimeseriesBlob}?${params.toString()}`;
    console.debug('Making blob metrics request to:', url);

    try {
      const response = await apiFetch(url, {
        method: 'GET',
      });

      const data = await handleApiResponse<MetricTimeseriesData>(response);
      return data;
    } catch (error) {
      console.error('Failed to fetch metrics timeseries from blob:', error);
      throw new Error(error instanceof Error ? error.message : 'Failed to fetch metrics data from blob storage');
    }
  }

  /**
   * Call backend Capsifi grid report endpoint and return raw JSON with no processing
   * This is a generic method that can be used for various grid reports
   * @param endpoint - The specific endpoint to use (vs-metrics, vs-transitions, or grid-report)
   * @param options - Optional parameters including gridId, itemId, and filters
   */
  private async getGridReportRaw(endpoint: string, options?: { gridId?: string; itemId?: string; filters?: string }): Promise<any> {
    // Read api settings for required headers
    let modelId = '';
    try {
      const savedSettings = sessionStorage.getItem('apiSettings');
      if (savedSettings) {
        const apiSettings = JSON.parse(savedSettings);
        modelId = apiSettings.selectedModelId || '';
      }
    } catch (e) {
      console.error('Error reading apiSettings from sessionStorage:', e);
    }

    const gridId = options?.gridId || '';
    const itemId = options?.itemId || '';
    const filters = options?.filters || '{}';

    const params = new URLSearchParams();
    if (gridId) params.append('gridId', gridId);
    if (itemId) params.append('itemId', itemId);
    if (filters) params.append('filters', filters);

    const url = `${API_CONFIG.baseUrl}/capsifi/${endpoint}?${params.toString()}`;
    console.debug(`Making Capsifi ${endpoint} request to:`, url);

    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    };
    if (modelId) headers[API_CONFIG.headers.modelId] = modelId;

    try {
      const response = await apiFetch(url, { method: 'GET', headers });
      return handleApiResponse<any>(response);
    } catch (error) {
      console.error(`Failed to fetch Capsifi ${endpoint}:`, error);
      throw new Error(error instanceof Error ? error.message : `Failed to fetch Capsifi ${endpoint}`);
    }
  }

  /**
   * Call backend Capsifi VS metrics and return raw JSON with no processing
   */
  async getCapsifiVsMetricsRaw(options?: { gridId?: string; itemId?: string; filters?: string }): Promise<any> {
    if (isCachingEnabled() && hasResponse('metrics')) {
      console.debug('Returning cached metrics');
      return getResponse('metrics');
    }

    // Defaults align with backend
    const defaultGridId = 'http://capsi.com.au/StrategicPlanningReports#ValueStreamObjectiveKeyResults';
    const defaultItemId = 'http://orbus_busarch.capsi.com.au/orbus_busarch/SP/Model#da671ee7-8b6e-4eae-9995-632971ac724f_VALUESTREAM_Sales_Pipelin';

    const gridId = options?.gridId || defaultGridId;
    const itemId = options?.itemId || defaultItemId;
    const filters = options?.filters || '{}';

    const result = await this.getGridReportRaw('vs-metrics', { gridId, itemId, filters });
    if (isCaptureEnabled('metrics')) {
      saveResponse('metrics', result);
      disableCapture('metrics');
    }
    return result;
  }

  /**
   * Call backend Capsifi VS transitions and return raw JSON with no processing
   * This endpoint retrieves transition data that shows connections between tasks and outcomes
   */
  async getCapsifiVsTransitionsRaw(options?: { itemId?: string; filters?: string }): Promise<any> {
    // Default grid ID for transitions
    const defaultGridId = 'http://capsi.com.au/StrategicPlanningReports#ValueStreamOutcomeTransitions';
    const defaultItemId = 'http://orbus_busarch.capsi.com.au/orbus_busarch/SP/Model#da671ee7-8b6e-4eae-9995-632971ac724f_VALUESTREAM_Sales_Pipelin';

    const gridId = defaultGridId;
    const itemId = options?.itemId || defaultItemId;
    const filters = options?.filters || '{}';

    return this.getGridReportRaw('vs-transitions', { gridId, itemId, filters });
  }
}